/**
 * Phase 23.1 — hand-off links + offline export (web).
 *
 * Everything here is keyless and billing-free:
 *  - Google Maps directions URLs (plain links, no SDK) for the whole
 *    route (only when every leg is ground-plausible — flight legs
 *    would render as absurd multi-day drives) and per-day navigation.
 *  - KML export built client-side from the card's coordinates: import
 *    into Google My Maps, or open in Organic Maps / Maps.me where the
 *    user's downloaded country pack makes the WHOLE itinerary work
 *    fully offline — better offline coverage than any API grants.
 */

const isNum = (v) => Number.isFinite(v);

export function mappableDays(card) {
    return (card?.days || []).filter(
        (d) => d?.city_coords && isNum(d.city_coords.lat) && isNum(d.city_coords.lng),
    );
}

function segIsFlight(day) {
    const t = String(
        (day?.blocks || []).map((b) => b?.transit_from_previous).find(Boolean) || "",
    ).toLowerCase();
    return /\bfly|flight|air\b/.test(t);
}

/** Whole-trip Google directions URL, or null when any leg is a flight
 *  (or fewer than 2 distinct points). Waypoint cap 9 per Google URL API. */
export function wholeRouteGoogleUrl(card) {
    const days = mappableDays(card);
    if (days.length < 2) return null;
    if (days.slice(1).some(segIsFlight)) return null;
    const pts = [];
    for (const d of days) {
        const p = `${d.city_coords.lat},${d.city_coords.lng}`;
        if (pts[pts.length - 1] !== p) pts.push(p);
    }
    if (pts.length < 2) return null;
    const capped = pts.length > 11 ? [...pts.slice(0, 10), pts[pts.length - 1]] : pts;
    const origin = capped[0];
    const destination = capped[capped.length - 1];
    const waypoints = capped.slice(1, -1).join("|");
    return (
        "https://www.google.com/maps/dir/?api=1" +
        `&origin=${origin}&destination=${destination}` +
        (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "") +
        "&travelmode=driving"
    );
}

/** Per-day navigation URL: the day's POIs chained as stops; falls back
 *  to a single-place link at the day's city. */
export function dayGoogleUrl(day) {
    if (!day?.city_coords || !isNum(day.city_coords.lat)) return null;
    const pois = (day.blocks || []).filter((b) => isNum(b?.lat) && isNum(b?.lng));
    if (pois.length === 0) {
        return `https://www.google.com/maps/search/?api=1&query=${day.city_coords.lat},${day.city_coords.lng}`;
    }
    const origin = `${day.city_coords.lat},${day.city_coords.lng}`;
    const destination = `${pois[pois.length - 1].lat},${pois[pois.length - 1].lng}`;
    const waypoints = pois.slice(0, -1).map((b) => `${b.lat},${b.lng}`).join("|");
    return (
        "https://www.google.com/maps/dir/?api=1" +
        `&origin=${origin}&destination=${destination}` +
        (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "") +
        "&travelmode=walking"
    );
}

const xmlEsc = (s) =>
    String(s ?? "").replace(/[<>&"']/g, (c) => (
        { "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]
    ));

/** Build a KML document from the card (day pins, POI pins, route line). */
export function buildKml(card) {
    const days = mappableDays(card);
    if (days.length === 0) return null;
    const name = xmlEsc(card?.title || card?.destination_summary || "Piqpath itinerary");
    const placemarks = [];
    const routeCoords = [];
    days.forEach((d, i) => {
        const { lat, lng } = d.city_coords;
        routeCoords.push(`${lng},${lat},0`);
        placemarks.push(
            `<Placemark><name>Day ${d.day || i + 1} · ${xmlEsc(d.city || "")}</name>` +
            `<description>${xmlEsc(d.title || "")}${d.story ? " — " + xmlEsc(d.story) : ""}</description>` +
            `<Point><coordinates>${lng},${lat},0</coordinates></Point></Placemark>`,
        );
        (d.blocks || []).forEach((b) => {
            if (!isNum(b?.lat) || !isNum(b?.lng)) return;
            placemarks.push(
                `<Placemark><name>${xmlEsc(b.activity || "Stop")}</name>` +
                `<description>Day ${d.day || i + 1}${b.category ? " · " + xmlEsc(b.category) : ""}</description>` +
                `<Point><coordinates>${b.lng},${b.lat},0</coordinates></Point></Placemark>`,
            );
        });
    });
    const route = routeCoords.length >= 2
        ? `<Placemark><name>Route</name><LineString><tessellate>1</tessellate>` +
          `<coordinates>${routeCoords.join(" ")}</coordinates></LineString></Placemark>`
        : "";
    return (
        `<?xml version="1.0" encoding="UTF-8"?>\n` +
        `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>` +
        `<name>${name}</name>${placemarks.join("")}${route}` +
        `</Document></kml>`
    );
}

export function downloadKml(card) {
    const kml = buildKml(card);
    if (!kml) return false;
    const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const base = String(card?.destination_summary || "itinerary")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "itinerary";
    a.download = `piqpath-${base}.kml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    return true;
}
